/**
 * Cookie consent banner. Two mounts: the marketing bundle raises it on every
 * page (src/marketing/src/App.tsx), and the SPA raises it on the homepage
 * (src/web/src/App.tsx).
 *
 * It was pulled back to the homepage alone on 2026-08-25 (73f7961d) on the
 * reasoning that a choice made there binds both bundles, since both read the
 * same stored answer. That is true of the CHOICE and false of the NOTICE: the
 * marketing head snippet starts the ad pixels for an undecided US visitor
 * before React mounts, so somebody who lands on /for/… or /blog/… and never
 * reaches the homepage was being tracked with no notice on screen and no exit
 * anywhere but a footer button. Notice has to sit where the collection starts,
 * so the marketing mount is back (2026-09-11 legal走查, A5).
 *
 * The SPA's non-homepage routes have the same shape of gap and are NOT closed
 * here — see docs/frontend-ui-standard.md. Closing them means lifting the
 * banner over Talk's composer and every docked page's door first, which is a
 * layout job, not a mount.
 *
 * Shows until the visitor clicks Accept or Reject. There is no "close"
 * affordance on purpose: dismissing or navigating away is not an answer, so the
 * only two outcomes are an explicit Accept or Reject.
 *
 * The copy comes in two versions because the banner has two different jobs, and
 * saying the wrong one is the actual legal risk here. Under `opt_in` it is
 * asking permission and nothing has loaded yet. Under `opt_out` — US visitors,
 * where the law's requirement is an exit rather than a question — the ad pixels
 * are already running by the time this renders, and the notice has to say so
 * plainly instead of pretending to ask. Which version applies is settled by the
 * head snippet before this mounts (window.__oriConsentRegime); it only ever
 * reports `opt_out` when something really did start, so the copy cannot claim
 * tracking that isn't happening. Both bundles load the same three tags, so both
 * pass the regime their own head snippet settled.
 *
 * Styling is fully inline so the banner renders identically on both apps
 * (Tailwind on marketing, design-system tokens on web) and so the two
 * buttons stay strictly symmetrical — same size, font, weight, and color —
 * as regulators require. It is a compact strip, not a block: two columns that
 * never wrap — the notice on the left, the two buttons stacked at its right —
 * so the bar stays about as tall as the copy itself and never reads as a wall
 * in front of the page. Stacking is what lets the copy keep a narrow measure:
 * on a phone it settles at roughly three short lines beside the pair.
 *
 * Two things keep it out of the product's way, and both are deliberate. It
 * stacks BELOW any dialog (see zIndex below), because a bottom sheet lands on
 * this same edge and its buttons must stay tappable. And it rides above a
 * page's fixed bottom dock rather than covering it, via the CSS hook below,
 * because a dock is permanent furniture — covering that would leave the door
 * unreachable for as long as the notice went unanswered.
 *
 * Two CSS hooks, one each way. `--cookie-banner-lift`: a page that keeps a
 * fixed bottom dock (the ember SPA's mobile door bars) sets it to the dock's
 * height next to the rules that decide when that dock is on screen, and the
 * banner sits on the dock's top edge instead of covering the page's main door.
 * Unset, the banner hugs the viewport bottom; when lifted, the bottom padding
 * sheds the safe-area inset because the dock below already spans it.
 * `--cookie-banner-h`: the banner's own rendered height, written on the root
 * element for as long as the strip is on screen and removed with it, so a page
 * whose first screen is exactly one viewport tall (the homepage's tree on a
 * phone, TreeHome.css) can pull its door up above the notice instead of under
 * it (2026-09-08). Measured, not assumed: the copy wraps differently per width
 * and regime, and the strip spans the safe area.
 *
 * Inside the SPA the banner is still raised on the homepage alone, so the only
 * lift that fires there is the homepage's own (TalkDock.css). The same rule on
 * the other docked pages is kept, not dead weight to delete: it is what makes
 * those pages safe the day this is mounted more widely.
 *
 * `reserveSpace` is the marketing bundle's answer to the same problem, and it
 * exists because that bundle has no dock to ride. A fixed strip covers the last
 * ~60px of the document, which on those pages is the footer's legal row — the
 * Privacy and Terms links and the Cookie preferences button, i.e. exactly the
 * controls a consent notice must not bury. With the flag the banner also emits
 * an in-flow spacer of its own height as the page's last box, so the document
 * scrolls that much further and nothing is trapped underneath. The SPA passes
 * nothing: its homepage is deliberately one viewport tall (TreeHome.css already
 * pads the ground by --cookie-banner-h) and a spacer there would invent a
 * scrollbar on a page built not to have one.
 *
 * onAccept lets each app boot its own trackers in place (load the ad pixels,
 * init PostHog) without a page reload. Reject loads nothing.
 *
 * onShown / onDecision are telemetry hooks, not behavior: the banner is the
 * first thing a pre-signup visitor sees, so the admin pre-signup funnel needs
 * its own denominator (how often it was raised) next to the two outcomes. They
 * are wired to each app's consent-free beacon — the click beacons alone can
 * only count the visitors who answered, never the ones who walked away.
 */

import { useEffect, useRef, useState } from 'react';

import { getCookieConsent, setCookieConsent } from './cookie-consent';

export type ConsentRegime = 'opt_in' | 'opt_out';

// Asking. Nothing non-necessary has loaded yet, and nothing will until Accept.
// Copy leads with what the necessary cookies are FOR (making the site work)
// before the disclosure, so the first sentence a visitor reads isn't a bare
// "we share your data" — the facts stay identical (拍板 2026-09-01).
// 2026-09-06 走查: four lines over the first screen's door on a phone. Two
// short sentences say the same facts; the buttons say what each one does.
const COPY_OPT_IN =
  'A few cookies make Ember work. Optional ones help us measure our ads.';

// Telling. The pixels are running as this is read, so the notice still says so
// plainly and leads with the way out, which is what makes it a notice and not
// a question dressed up as one.
const COPY_OPT_OUT =
  'A few cookies make Ember work. Optional ones that help us measure our ads ' +
  'are on now; “Reject” turns them off.';

const BUTTON_STYLE: React.CSSProperties = {
  // Stacked in a column at the right edge, so they take one button's width
  // rather than half the bar. The column stretches both to its widest member
  // and minWidth sets the floor, which is what keeps the two strictly
  // symmetrical — the rule the buttons cannot break, at any size.
  flex: '0 0 auto',
  minWidth: 78,
  padding: '5px 14px',
  borderRadius: 999,
  border: '1px solid rgba(74, 43, 20, 0.28)',
  background: '#fffdf8',
  color: '#2b2417',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.2,
  cursor: 'pointer',
};

export function CookieBanner({
  onAccept,
  onReject,
  onShown,
  onDecision,
  regime = 'opt_in',
  deferUntilMounted = false,
  reserveSpace = false,
}: {
  onAccept?: () => void;
  /** Called on Reject, for the retraction the copy promises. */
  onReject?: () => void;
  /** Called once when the banner actually renders (i.e. no choice on record). */
  onShown?: () => void;
  /** Called with the choice the visitor made. */
  onDecision?: (decision: 'accepted' | 'rejected') => void;
  /** Which copy is true for this visitor. Defaults to the asking version. */
  regime?: ConsentRegime;
  /**
   * Keep the server and the browser's first render identical when the banner
   * lives inside a hydrated root. Consent is stored in localStorage, which the
   * server cannot read; after mount the browser reveals the banner if needed.
   */
  deferUntilMounted?: boolean;
  /**
   * Emit an in-flow spacer of the strip's own height as well as the fixed
   * strip, so the document can scroll clear of it (see the header). Set by a
   * bundle whose pages end in content the strip would otherwise cover; left
   * off where the page already reserves the room itself.
   */
  reserveSpace?: boolean;
}): React.ReactElement | null {
  const [visible, setVisible] = useState(() => (
    deferUntilMounted ? false : getCookieConsent() === null
  ));
  const shownRef = useRef(false);
  const stripRef = useRef<HTMLDivElement>(null);

  // --cookie-banner-h (see the header): the strip's height on the root while it
  // is on screen, kept current as the copy rewraps; gone the moment it is.
  useEffect(() => {
    if (!visible) return undefined;
    const root = document.documentElement;
    const el = stripRef.current;
    if (!el) return undefined;
    const write = (): void => { root.style.setProperty('--cookie-banner-h', `${el.offsetHeight}px`); };
    write();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(write) : null;
    ro?.observe(el);
    window.addEventListener('resize', write);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', write);
      root.style.removeProperty('--cookie-banner-h');
    };
  }, [visible]);

  useEffect(() => {
    if (!deferUntilMounted) return;
    setVisible(getCookieConsent() === null);
  }, [deferUntilMounted]);

  useEffect(() => {
    if (!visible || shownRef.current) return;
    shownRef.current = true;
    onShown?.();
  }, [visible, onShown]);

  if (!visible) return null;

  const accept = (): void => {
    setCookieConsent('accepted');
    setVisible(false);
    onDecision?.('accepted');
    onAccept?.();
  };

  const reject = (): void => {
    setCookieConsent('rejected');
    setVisible(false);
    // Order matters only in that both must happen: the decision beacon is what
    // tells the server to drop this person's stored match keys, and onReject is
    // what clears the ones already in this browser.
    onDecision?.('rejected');
    onReject?.();
  };

  return (
    <>
      {/* The scroll room the fixed strip below would otherwise steal from the
          end of the document. Height is read from the same custom property the
          strip measures itself into, so the two stay equal through every
          rewrap without a second measurement. */}
      {reserveSpace ? (
        <div aria-hidden style={{ height: 'var(--cookie-banner-h, 0px)', flex: '0 0 auto' }} />
      ) : null}
      <div
        ref={stripRef}
        role="dialog"
        aria-label="Cookie consent"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 'var(--cookie-banner-lift, 0px)',
          // Above the page's own furniture (bottom docks at 40, nav at 50/60),
          // below every dialog (the SPA's Modal/BottomSheet at 90, marketing's
          // MessagesDoor at 100). A sheet docks to this same bottom edge, so it
          // has to be able to cover this strip — otherwise its actions sit
          // underneath and cannot be tapped, which is the one thing a consent
          // notice must never do to the product. The strip comes straight back
          // when the dialog closes: deferred, never dismissed. This was
          // 2147483000 ("always win"), which is what put it over those sheets.
          // `--cookie-banner-z` lets a page put its own open sheet over the
          // strip (the homepage composer's list and boxes, TreeHome.css).
          zIndex: 'var(--cookie-banner-z, 80)',
          // The strip wears the site's own cream instead of the inverted dark
          // bar it launched with — the dark block read as a warning wall on the
          // warm first screen (拍板 2026-09-01). Still inline, still identical
          // on both bundles; the top hairline is what separates it from a page
          // that shares its background.
          background: '#fdf8ec',
          color: '#2b2417',
          borderTop: '1px solid rgba(74, 43, 20, 0.16)',
          boxShadow: '0 -2px 20px rgba(74, 43, 20, 0.14)',
          // Lifted above a dock, the inset below is the dock's to cover, so the
          // bottom padding collapses back to the plain 8px.
          padding: '8px max(16px, env(safe-area-inset-left)) max(8px, calc(8px + env(safe-area-inset-bottom, 0px) - var(--cookie-banner-lift, 0px)))',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            // A narrow measure, centred: the copy wraps to a few short lines
            // beside the button column instead of stretching into one long
            // ribbon the eye has to track all the way across the viewport.
            maxWidth: 680,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <p
            style={{
              // Takes every pixel the button column doesn't need. minWidth:0
              // lets it actually shrink to that share instead of holding the
              // pair off the right edge on a narrow phone.
              flex: '1 1 auto',
              minWidth: 0,
              margin: 0,
              fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
              fontSize: 12,
              lineHeight: 1.45,
            }}
          >
            {regime === 'opt_out' ? COPY_OPT_OUT : COPY_OPT_IN}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '0 0 auto' }}>
            <button type="button" style={BUTTON_STYLE} onClick={accept}>
              Accept
            </button>
            <button type="button" style={BUTTON_STYLE} onClick={reject}>
              Reject
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
